import { CampaignInfo, CurrencyMapping } from "@store";
import * as cn from "classnames";
import * as React from "react";
import { ContentLoadBlockComponent } from "./ContentLoadBlock";
import "./DashboardTable.css";
import { formatDate, ReportUrl, formatNumber } from "@common";
import { Link } from "react-router-dom";
import { SvgIcon } from "./SvgIcon";
import { Triangle, SidePanelIconEditOff, SvgIconDeleteOff, SideMenuIconReportOff } from "@icons";
import { LongNameComponent } from "./LongNameComponent";

const mockColStatus = [
    { colSortUrl: "status", colName: "Status" },
    { colSortUrl: "campaignId", colName: "ID" },
    { colSortUrl: "campaignName", colName: "Name" },
    { colSortUrl: "startDate", colName: "Start Date" },
    { colSortUrl: "endDate", colName: "End Date" },
    { colSortUrl: "budget", colName: "Budget" }
];

export interface DashboardTableComponentProps {
    campaigns: CampaignInfo[];
    loadingCampaigns: boolean;
    selectedCampaignId: string;
    items: number;
    selectedIndex: number;
    onSelectCampaign: (campaignId: string) => void;
    onPageChange: (page: number) => void;
    onSort: (sortBy: string) => void;
    currencies: CurrencyMapping[];
    onDelete: (clientId: string, campaignId: string) => void;
    onLinkToCampaignEditPage: (clientId: string, campaignId: string) => void;
    onDoubleClickCampaign: (campaignId: string, clientId: string) => void;
    clientId: string;
}

export interface DashboardTableComponentState {
    currentSort: {
        name: string;
        sortType: "desc" | "asc";
    };
}

export class DashboardTableComponent extends React.Component<
    DashboardTableComponentProps,
    DashboardTableComponentState
> {
    constructor(props: DashboardTableComponentProps) {
        super(props);
        this.state = {
            currentSort: {
                name: undefined,
                sortType: undefined
            }
        };
    }

    async handleSort(colSortUrl: string, colName: string) {
        if (this.state.currentSort.name === colName) {
            await this.setState({
                ...this.state,
                currentSort: {
                    ...this.state.currentSort,
                    sortType: this.state.currentSort.sortType === "desc" ? "asc" : "desc"
                }
            });
        } else {
            await this.setState({
                ...this.state,
                currentSort: {
                    ...this.state.currentSort,
                    name: colName,
                    sortType: "desc"
                }
            });
        }

        this.props.onSort(`${colSortUrl} ${this.state.currentSort.sortType}`);
    }

    // Local Variable for Double Click
    private timer: any = 0;
    private delay = 200;
    private prevent: any = false;

    doClickAction(campaignId: string) {
        this.props.onSelectCampaign(campaignId);
    }

    doDoubleClickAction(clientId: string, campaignId: string) {
        this.props.onDoubleClickCampaign(campaignId, clientId);
    }

    handleClick(campaignId: string) {
        let self = this;

        this.timer = setTimeout(function() {
            if (!self.prevent) {
                self.doClickAction(campaignId);
            }
            self.prevent = false;
        }, this.delay);
    }

    handleDoubleClick(campaignId: string, clientId: string) {
        clearTimeout(this.timer);
        this.prevent = true;
        this.doDoubleClickAction(campaignId, clientId);
    }

    renderRow(campaign: CampaignInfo, index: number) {
        const { onDelete, onLinkToCampaignEditPage, clientId } = this.props;
        const isNullOrUndefined = (val: any) => val === null || val === undefined;
        const campaignCurrency =
            this.props.currencies && this.props.currencies.find(c => c.isoCode === campaign.currency);

        return (
            <tr
                className={cn(
                    "table-row",
                    campaign.campaignId === this.props.selectedCampaignId ? "active" : "",
                    campaign.status === "archived" ? "disabled" : ""
                )}
                key={index}
                onClick={campaign.status !== "archived" ? this.handleClick.bind(this, campaign.campaignId) : undefined}
                onDoubleClick={
                    campaign.status !== "archived" && campaign.status !== "draft"
                        ? this.handleDoubleClick.bind(this, campaign.campaignId, campaign.clientId)
                        : undefined
                }
            >
                <td className="status-column">
                    <span
                        className={cn("status-circles dashboard-custom-status-circles", campaign.status + "-circle")}
                    />
                    <span className="status-label">{campaign.status}</span>
                </td>
                <td>{campaign.status !== "draft" ? campaign.campaignId : "-"}</td>
                <td>{campaign.campaignName ? <LongNameComponent name={campaign.campaignName} /> : ""}</td>
                <td>{campaign.startDate ? formatDate(campaign.startDate) : "-"}</td>
                <td>{campaign.endDate ? formatDate(campaign.endDate) : "-"}</td>
                <td>
                    {isNullOrUndefined(campaign.budget)
                        ? "-"
                        : campaignCurrency
                            ? formatNumber(campaign.budget.toFixed(2)) + " " + campaignCurrency.symbol
                            : formatNumber(campaign.budget.toFixed(2))}
                </td>
                <td>
                    {campaign.status === "draft" && (
                        <>
                            <span
                                className={cn("dashboard__optional-icons")}
                                onClick={e => onLinkToCampaignEditPage(clientId, campaign.campaignId)}
                            >
                                <SvgIcon svg={SidePanelIconEditOff} />
                            </span>
                            <span
                                className={cn("dashboard__optional-icons")}
                                onClick={() => onDelete(clientId, campaign.campaignId)}
                            >
                                <SvgIcon svg={SvgIconDeleteOff} />
                            </span>
                        </>
                    )}

                    {(campaign.status === "running" || campaign.status === "completed") && (
                        <Link
                            to={ReportUrl.format(
                                {
                                    campaignId: campaign.campaignId,
                                    clientId: campaign.clientId || "direct",
                                    tab: "dynamic"
                                },
                                { fromDate: formatDate(campaign.startDate), toDate: formatDate(campaign.endDate) }
                            )}
                            className="dashboard__optional-icons view-report-link"
                        >
                            <SvgIcon svg={SideMenuIconReportOff} />
                        </Link>
                    )}
                </td>
            </tr>
        );
    }

    render() {
        const { campaigns, loadingCampaigns, onPageChange } = this.props;
        let { items, selectedIndex } = this.props;
        let pages = campaigns && Math.ceil(campaigns.length / items);
        return (
            <>
                <table className="dashboard-table">
                    <thead>
                        <tr className="table-row">
                            {mockColStatus.map((el, index) => (
                                <th
                                    key={index}
                                    className={cn(
                                        "sort-col",
                                        this.state.currentSort.sortType === "desc" ? "active" : ""
                                    )}
                                    onClick={e => this.handleSort(el.colSortUrl, el.colName)}
                                >
                                    {el.colName}
                                    {this.state.currentSort.name === el.colName && (
                                        <span className="sort-icon">&#10095;</span>
                                    )}
                                </th>
                            ))}
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        <ContentLoadBlockComponent loading={loadingCampaigns}>
                            {campaigns && campaigns.length > 0 ? (
                                campaigns
                                    .slice(selectedIndex * items, (selectedIndex + 1) * items)
                                    .map((el: CampaignInfo, index: number) => this.renderRow(el, index))
                            ) : (
                                <tr>
                                    <td colSpan={9} style={{ fontSize: "1rem" }}>
                                        There are no campaigns!
                                    </td>
                                </tr>
                            )}
                        </ContentLoadBlockComponent>
                    </tbody>
                </table>
                <div className="pagination">
                    {pages > 1 &&
                        (() => {
                            const Items = [];
                            Items.push(
                                <div key="uniq-1">
                                    <button
                                        className={
                                            "pagination--switch" +
                                            (0 === selectedIndex ? " pagination--switch-isActive" : "")
                                        }
                                        onClick={() => onPageChange(1)}
                                        key={"first"}
                                    >
                                        First
                                    </button>

                                    <button
                                        onClick={() => onPageChange(selectedIndex)}
                                        disabled={0 === selectedIndex}
                                        className="Paginator__arrows page-prev"
                                        key={"left-arrow"}
                                    >
                                        <SvgIcon svg={Triangle} />
                                    </button>
                                </div>
                            );
                            for (let i = 0; i < pages; i++) {
                                Items.push(
                                    <button
                                        className={
                                            "pagination--switch" +
                                            (i == selectedIndex ? " pagination--switch-isActive" : "")
                                        }
                                        onClick={() => onPageChange(i + 1)}
                                        key={`pagination--switch-${i}`}
                                    >
                                        {i + 1}
                                    </button>
                                );
                            }
                            Items.push(
                                <div key="uniq-2">
                                    <button
                                        onClick={() => onPageChange(selectedIndex + 2)}
                                        disabled={pages - 1 === selectedIndex}
                                        className="Paginator__arrows page-next"
                                        // key={`last-${pages}`}
                                    >
                                        <SvgIcon svg={Triangle} />
                                    </button>

                                    <button
                                        className={
                                            "pagination--switch" +
                                            (pages - 1 === selectedIndex ? " pagination--switch-isActive" : "")
                                        }
                                        onClick={() => onPageChange(pages)}
                                        // key={`right-arrow-${pages}`}
                                    >
                                        Last
                                    </button>
                                </div>
                            );

                            return Items;
                        })()}
                </div>
            </>
        );
    }
}
